import { isEmpty } from 'lodash';
import React, { useCallback, useEffect } from 'react';

import {
  getLastWatchedEpgs,
  getListsForManySubcategories,
  getMostWatchedEpgs,
  translationPacket
} from 'common/actions';
import { Card, TextCard } from 'common/components/cards';
import { Stripe } from 'common/components/stripe';
import { ViewWithDetails } from 'common/components/view-with-details';
import { getCategoryBackground } from 'common/config/archive-categories-bg';
import { ROUTE_ARCHIVE } from 'common/config/constants';
import variables from 'common/config/variables';
import {
  Category,
  Epg,
  EpgsStore,
  IViewWithDetailsStripeItem,
  LiveChannels
} from 'common/constants/data-types';
import protect from 'common/HoC/protect';
import { getChannelLogo } from 'common/services/helpers';
import { usePageNavigation, usePlayerActions } from 'common/utils/hooks';
import { memoizeLast } from 'common/utils/memoizeLast';
import { PromiseDispatch, StoreState } from 'index';
import { connect } from 'react-redux';

interface StateProps {
  i18n: typeof translationPacket;
  liveChannels: LiveChannels;
  recorded: EpgsStore['recorded'];
  lastWatched: Epg[];
  mostWatched: Epg[];
  logos: any;
}

interface ArchiveProps extends StateProps {
  getListsForManySubcategories: (category: Category) => Promise<any>;
  setVideoOptions: any;
  playVideo: (currentepg: Epg, options?: any) => void;
  getMostWatchedEpgs: PromiseDispatch;
  getLastWatchedEpgs: PromiseDispatch;
}

function Archive({
  i18n,
  recorded,
  lastWatched,
  mostWatched,
  liveChannels,
  getMostWatchedEpgs,
  getLastWatchedEpgs,
  logos
}: ArchiveProps) {
  const navigate = usePageNavigation();
  const { playVideoFromContinueWatching: playVideo } = usePlayerActions();

  useEffect(() => {
    const id = setTimeout(async () => {
      await getLastWatchedEpgs();
      await getMostWatchedEpgs();
    }, variables.THROTTLE_TIMEOUT);
    return () => {
      clearTimeout(id);
    };
  }, []);

  const openPlayer = useCallback((asset: IViewWithDetailsStripeItem) => {
    playVideo(asset, { continueWatching: true, isFullscreen: true });
  }, []);

  const renderEpgItem = useCallback(
    (item: Epg) => (
      <Card
        key={`archive-card-${item.eventid}`}
        epg={item}
        logo={getChannelLogo(item.chan_id, logos)}
        testId={`archive-epg-card-${item.eventid}`}
        i18n={i18n}
      />
    ),
    [logos, i18n]
  );

  const getSelectedAssetData = useCallback(
    (asset: IViewWithDetailsStripeItem) => {
      if (asset.chan_id) {
        return {
          epg: asset,
          // Extract resolution from liveChannels
          resolution: liveChannels[asset.chan_id]?.resolution
        };
      }
      return undefined;
    },
    [liveChannels]
  );

  const renderCategoryCard = useCallback(
    (item: Category, idx: number) => (
      <TextCard
        key={`archive-category-card-${idx}`}
        title={item.name}
        bgUrl={getCategoryBackground(idx)}
        testId={`archive-category-card-${idx}`}
      />
    ),
    []
  );

  const openCategory = useCallback((category: Category) => {
    navigate(`${ROUTE_ARCHIVE}/${category.id}`);
  }, []);

  const getFocusKeyOnCategories = useCallback(
    (item: Category, index: number) => `archive-category-${item.position}-${index}`,
    []
  );

  const renderCategories = useCallback(() => {
    const { categories } = recorded;

    if (isEmpty(categories)) {
      return null;
    }
    const categoriesList = Object.values(categories).map((e) => ({
      ...e,
      id: e.position.toString()
    }));

    return (
      <Stripe
        key="archive-category-stripe"
        items={categoriesList}
        renderItem={renderCategoryCard}
        onStripeItemPress={openCategory}
        getFocusKey={getFocusKeyOnCategories}
      />
    );
  }, [recorded.categories]);

  const renderMostWatched = useCallback(() => {
    if (isEmpty(mostWatched)) {
      return null;
    }

    return (
      <Stripe
        key="most-watched-stripe"
        items={mostWatched}
        renderItem={renderEpgItem}
        title={i18n.common.mostWatched}
        onStripeItemPress={openPlayer}
        testId="most-watched-stripe"
      />
    );
  }, [mostWatched]);

  const renderLastWatched = useCallback(() => {
    if (isEmpty(lastWatched)) {
      return null;
    }

    return (
      <Stripe
        key="last-watched-stripe"
        items={lastWatched}
        renderItem={renderEpgItem}
        title={i18n.channelList.lastWatched}
        onStripeItemPress={openPlayer}
        testId="last-watched-stripe"
      />
    );
  }, [lastWatched]);

  return (
    <ViewWithDetails focusKey={location.pathname} getSelectedAssetData={getSelectedAssetData}>
      {[renderCategories(), renderMostWatched(), renderLastWatched()]}
    </ViewWithDetails>
  );
}

const visibleOnly = (list: any) => list?.filter((e: any) => e.visible);
const visibleLastWatched = memoizeLast(visibleOnly);
const visibleMostWatched = memoizeLast(visibleOnly);

const mapStateToProps = ({
  i18n,
  epgs,
  lastWatched,
  mostWatched,
  liveChannels,
  logos
}: StoreState) => ({
  i18n,
  recorded: epgs.recorded as EpgsStore['recorded'],
  lastWatched: visibleLastWatched(lastWatched),
  mostWatched: visibleMostWatched(mostWatched),
  liveChannels,
  logos
});
export default React.memo(
  protect(
    connect(mapStateToProps, {
      getListsForManySubcategories,
      getLastWatchedEpgs,
      getMostWatchedEpgs
    })(Archive)
  )
);
